import type { OperationalHealthStatus, VerificationKind } from "../../api/types";
import type { StatusTone } from "../../formatting/status";

/**
 * Feature-local presentation for "Estado del sistema" (REDISENO-PANEL.md
 * §8.3, panel-redesign-v3 T8). The backend sends only closed CODES
 * (`status`/`verificationKind`/`guidanceCode`) — never free text — so this is
 * the ONE place that turns them into the actual Spanish copy the screen
 * shows, the same code/copy separation already established for
 * `OperationalMetric.blockedBy` ("F2-01"). Pure — no rendering, no I/O.
 */

/** `available`/`degraded`/`pending` -> label + `StatusBadge` tone. Never color alone (§10). Used by the Cuenta card's "Estado"/"Servicio" fields. */
export function healthStatusPresentation(status: OperationalHealthStatus): { label: string; tone: StatusTone } {
  switch (status) {
    case "available":
      return { label: "Disponible", tone: "positive" };
    case "degraded":
      return { label: "Degradado", tone: "warning" };
    case "pending":
    default:
      return { label: "Pendiente", tone: "unavailable" };
  }
}

/**
 * `available`/`degraded`/`pending` -> the Integraciones row pill
 * (REDISENO-PANEL.md §8.3: "Estados: available -> 'Funciona', degraded ->
 * 'Revisar', pending -> 'Próximamente'") — a DIFFERENT wording than
 * `healthStatusPresentation` above (that one describes the Cuenta card's own
 * identity/service state, this one describes a third-party integration row).
 */
export function integrationStatusPresentation(status: OperationalHealthStatus): { label: string; tone: StatusTone } {
  switch (status) {
    case "available":
      return { label: "Funciona", tone: "positive" };
    case "degraded":
      return { label: "Revisar", tone: "warning" };
    case "pending":
    default:
      return { label: "Próximamente", tone: "unavailable" };
  }
}

/** How the `status` above was established — never itself a description of an active probe (§5.7: "must not be worded as an active end-to-end health probe"). */
export function verificationKindLabel(kind: VerificationKind): string {
  switch (kind) {
    case "request":
      return "Solicitud autenticada";
    case "query":
      return "Consulta de base de datos";
    case "configuration":
    default:
      return "Configuración";
  }
}

/**
 * Guidance copy by closed code (`IntegrationStatus.guidanceCode`), rewritten
 * in plain language (REDISENO-PANEL.md §8.3: "reescribir GUIDANCE_COPY en
 * lenguaje simple manteniendo los mismos códigos") — the wording matches the
 * prototype's own literal `ints` guide strings (`Panel Rediseño.dc.html`,
 * líneas 1196-1201) wherever a code has a direct prototype counterpart.
 */
const GUIDANCE_COPY: Record<string, string> = {
  "request-path-verified": "Tu sesión llegó autenticada; el panel responde con normalidad.",
  "identity-query-verified": "La consulta de la cuenta respondió correctamente.",
  "identity-query-failed": "No pudimos confirmar los datos de tu cuenta en esta solicitud; no se hizo ninguna otra verificación.",
  "configuration-present": "Configuración presente. No se hizo una prueba de entrega.",
  "configuration-partial": "Configuración incompleta. No se hizo una prueba de entrega.",
  "configuration-missing": "Todavía no se configuró esta integración. No se hizo una prueba de entrega.",
  "not-implemented": "Aún no está disponible en el sistema.",
};

/** Unknown future guidance codes fall back to a generic, still-honest message rather than crashing or inventing confidence. */
export function guidanceCopy(code: string): string {
  return GUIDANCE_COPY[code] ?? "Sin evidencia adicional registrada para esta integración.";
}

/** Recognizable labels for the integration keys this screen renders (REDISENO-PANEL.md §8.3's own literal mapping). */
const INTEGRATION_LABELS: Record<string, string> = {
  "admin-api": "Acceso al panel",
  database: "Base de datos",
  "acs-email": "Envío de correo",
  "service-bus": "Cola de envíos",
  "content-blob": "Almacenamiento de contenido",
  "graph-mailbox": "Buzón de respuestas",
};

/** Unknown future integration keys fall back to the raw key rather than crashing. */
export function integrationLabel(key: string): string {
  return INTEGRATION_LABELS[key] ?? key;
}

/**
 * Plural metric labels for the "Métricas" card's chips (§8.3: "`metricLabel`
 * en plural: Aperturas, Clics…"). Feature-local — `formatting/metrics.ts`'s
 * own `metricLabel` is singular (shared with Telemetría/Inicio's own
 * singular usages), so this is a SEPARATE, smaller map rather than a second
 * mode bolted onto that shared function.
 */
const METRIC_LABELS_PLURAL: Record<string, string> = {
  encolado: "Encolados",
  error: "Errores",
  entregado: "Entregados",
  apertura: "Aperturas",
  clic: "Clics",
  rebote_blando: "Rebotes blandos",
  rebote_duro: "Rebotes duros",
  queja_spam: "Quejas de spam",
  suprimidos: "Suprimidos",
};

/** Unknown future metric keys fall back to the raw key rather than crashing. */
export function metricLabelPlural(key: string): string {
  return METRIC_LABELS_PLURAL[key] ?? key;
}
